/**
 * DataConfidenceDot — unified confidence indicator used across the
 * screener, stock detail header, suggested stock cards, portfolio
 * holdings, and anywhere else we show a coloured confidence pill.
 *
 *   🟢 high     → 3+ analysed annual reports AND eps+pb+roe non-null
 *   🟡 medium   → 1-2 analysed annuals, or 3+ with partial fundamentals
 *   🟠 low      → TradingView fundamentals only (no analysed annuals)
 *   🔴 minimal  → no TV fundamentals AND no analysed annuals
 *
 * Tooltip content (Fix 2 — 2026-05-01 UX pass): previously showed two
 * raw numbers with no context. Now renders a guided block with:
 *   • bullet-style context lines (✓ / 📊 / ⚠ / ❌)
 *   • a concrete "what to do next" hint for sub-optimal levels
 *   • data source + last-updated timestamp so admins know when the
 *     score was last refreshed
 *   • a "Go to reports" link into the Reports tab of the stock
 *
 * Intentionally kept self-contained — no auxiliary fetches. Callers
 * pass every field they already have (level, counts, optional
 * hasTradingViewFundamentals, optional lastUpdated). Props missing on
 * legacy call sites fall back gracefully (hidden rows) so rolling this
 * out doesn't regress the simpler surfaces.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowRight, CheckCircle2, BarChart3, AlertTriangle, XCircle, Lightbulb, CalendarClock } from "lucide-react";
import { formatDate } from "@/lib/api";

const CONFIG = {
  high: {
    title: "High Confidence",
    titleIcon: "🟢",
    cls: "bg-[hsl(var(--success))] ring-[hsl(var(--success))]/30",
    aria: "High data confidence",
  },
  medium: {
    title: "Medium Confidence",
    titleIcon: "🟡",
    cls: "bg-amber-400 ring-amber-400/30",
    aria: "Medium data confidence",
  },
  low: {
    title: "Low Confidence",
    titleIcon: "🟠",
    cls: "bg-orange-500 ring-orange-500/30",
    aria: "Low data confidence",
  },
  minimal: {
    title: "Minimal Data",
    titleIcon: "🔴",
    cls: "bg-[hsl(var(--destructive))] ring-[hsl(var(--destructive))]/30",
    aria: "Minimal data",
  },
};

/** Small row used inside the tooltip body. Pairs a status icon with a
 * tight one-line description. Kept as a local component so the tooltip
 * stays readable without a JSX jungle. */
const TooltipRow = ({ Icon, tone = "foreground", children, testId }) => {
  const toneCls =
    tone === "success" ? "text-[hsl(var(--success))]"
    : tone === "warning" ? "text-amber-500"
    : tone === "danger" ? "text-[hsl(var(--destructive))]"
    : tone === "muted" ? "text-muted-foreground"
    : "text-foreground";
  return (
    <div className="flex items-start gap-1.5 leading-snug" data-testid={testId}>
      <Icon className={`h-3 w-3 mt-0.5 shrink-0 ${toneCls}`} />
      <span className="flex-1">{children}</span>
    </div>
  );
};

export const DataConfidenceDot = ({
  level = "low",
  annualReports = 0,
  interimReports = 0,
  hasTradingViewFundamentals,
  lastUpdated,
  companyCode,
  size = "md",
  testId,
}) => {
  const cfg = CONFIG[level] || CONFIG.low;
  const dotSize = size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3";
  // Defensive default for TV coverage — if the caller doesn't pass it,
  // infer from level (low/medium/high imply at least TV coverage; only
  // "minimal" means no TV data).
  const tvAvailable = hasTradingViewFundamentals ?? (level !== "minimal");

  // Rows specific to the confidence level — order and tone chosen so
  // the most important line is always first.
  const rows = [];
  if (level === "high") {
    rows.push(
      <TooltipRow
        key="annuals"
        Icon={CheckCircle2}
        tone="success"
        testId="data-confidence-row-annuals-high"
      >
        <strong>{annualReports}</strong> annual report{annualReports === 1 ? "" : "s"} analysed by Claude AI
      </TooltipRow>,
    );
    if (interimReports > 0) {
      rows.push(
        <TooltipRow key="interims" Icon={CheckCircle2} tone="success">
          <strong>{interimReports}</strong> interim report{interimReports === 1 ? "" : "s"} on file
        </TooltipRow>,
      );
    }
    if (tvAvailable) {
      rows.push(
        <TooltipRow key="tv" Icon={BarChart3} tone="muted">
          TradingView fundamentals also available
        </TooltipRow>,
      );
    }
  } else if (level === "medium") {
    rows.push(
      <TooltipRow
        key="annuals"
        Icon={CheckCircle2}
        tone="success"
        testId="data-confidence-row-annuals-medium"
      >
        <strong>{annualReports}</strong> annual report{annualReports === 1 ? "" : "s"} analysed by Claude AI
      </TooltipRow>,
    );
    if (interimReports > 0) {
      rows.push(
        <TooltipRow key="interims" Icon={CheckCircle2} tone="success">
          <strong>{interimReports}</strong> interim report{interimReports === 1 ? "" : "s"} analysed
        </TooltipRow>,
      );
    }
    if (tvAvailable) {
      rows.push(
        <TooltipRow key="tv" Icon={BarChart3} tone="muted">
          TradingView fundamentals available
        </TooltipRow>,
      );
    }
    rows.push(
      <TooltipRow key="hint" Icon={Lightbulb} tone="warning">
        Upload more annual reports to improve accuracy
      </TooltipRow>,
    );
  } else if (level === "low") {
    rows.push(
      <TooltipRow key="tv" Icon={BarChart3} tone="muted" testId="data-confidence-row-tv-low">
        TradingView fundamentals only
      </TooltipRow>,
    );
    rows.push(
      <TooltipRow key="no-annuals" Icon={XCircle} tone="danger">
        No annual reports analysed
      </TooltipRow>,
    );
    rows.push(
      <TooltipRow key="hint" Icon={Lightbulb} tone="warning">
        Upload annual reports on the Reports tab to improve scoring accuracy
      </TooltipRow>,
    );
  } else {
    // minimal
    rows.push(
      <TooltipRow key="warn" Icon={AlertTriangle} tone="danger" testId="data-confidence-row-minimal-warn">
        Very limited data available
      </TooltipRow>,
    );
    rows.push(
      <TooltipRow key="no-tv" Icon={XCircle} tone="danger">
        No TradingView coverage
      </TooltipRow>,
    );
    rows.push(
      <TooltipRow key="no-annuals" Icon={XCircle} tone="danger">
        No annual reports analysed
      </TooltipRow>,
    );
    rows.push(
      <TooltipRow key="hint" Icon={Lightbulb} tone="warning">
        Score may not be reliable for this stock
      </TooltipRow>,
    );
  }

  return (
    <Tooltip delayDuration={120}>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          role="img"
          aria-label={cfg.aria}
          data-testid={testId || `data-confidence-${companyCode || ""}`}
          className="inline-flex items-center justify-center rounded-full p-0.5 cursor-help focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          onClick={(e) => e.stopPropagation()}
        >
          <span
            className={`block rounded-full ring-2 ring-offset-1 ring-offset-background ${dotSize} ${cfg.cls}`}
            data-testid={`data-confidence-dot-${level}`}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        className="w-72 p-3 text-xs space-y-2"
        data-testid="data-confidence-tooltip"
      >
        {/* Title row — emoji + bold label. Emoji provides instant colour
            match with the dot the user just hovered. */}
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-border">
          <span className="text-base leading-none">{cfg.titleIcon}</span>
          <span
            className="text-sm font-semibold"
            data-testid={`data-confidence-title-${level}`}
          >
            {cfg.title}
          </span>
        </div>

        {/* Context-aware body rows. */}
        <div className="space-y-1">{rows}</div>

        {/* Last-updated footer — omitted when the caller didn't supply a
            timestamp (nothing worse than showing "Last updated: —"). */}
        {lastUpdated && (
          <div className="pt-1.5 border-t border-border">
            <TooltipRow Icon={CalendarClock} tone="muted" testId="data-confidence-row-last-updated">
              Last updated: <strong>{formatDate(lastUpdated)}</strong>
            </TooltipRow>
          </div>
        )}

        {companyCode && (
          <Link
            to={`/stocks/${companyCode}?tab=reports`}
            className="inline-flex items-center gap-1 text-primary hover:underline mt-1"
            data-testid="data-confidence-go-to-reports"
            onClick={(e) => e.stopPropagation()}
          >
            Go to reports tab <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

export default DataConfidenceDot;
